import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { MicrosoftTeamsCallQueue, MicrosoftTeamsCallQueueView } from '../shared/models/generated/ziroModels';
import { MicrosoftTenantsStateContext } from '../shared/contexts/microsoft-tenants-state.context';
import { ToastService } from '../shared/services/toast.service';
import { AuthenticationContext } from '../shared/contexts/authentication.context';
import { CallQueuesResource } from '../shared/resources/call-queues.resource';

@Injectable()
export class TeamsCallQueuesContext implements OnDestroy {
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _toastService = inject(ToastService);
  private _authenticationContext = inject(AuthenticationContext);
  private _callQueuesResource = inject(CallQueuesResource);

  private _stateSource = new ReplaySubject<MicrosoftTeamsCallQueue[]>(1);
  state$ = this._stateSource.asObservable();

  private _tenantId: string;
  private _subscriptions = new Subscription();

  constructor() {
    const sub = this._authenticationContext.state$
      .pipe(
        take(1),
        switchMap(() =>
          this._tenantsStateContext.cachedTenantState$.pipe(
            filter((tenant) => !!tenant?.tenantId),
            take(1),
          ),
        ),
        switchMap((tenant) => {
          this._tenantId = tenant.tenantId.toString();
          return this._callQueuesResource.getAll(this._tenantId);
        }),
      )
      .subscribe((queues) => this._stateSource.next(queues));

    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  refreshAllData(): Observable<void> {
    return this._callQueuesResource.getAll(this._tenantId).pipe(
      tap((queues: MicrosoftTeamsCallQueue[]): void => this._stateSource.next(queues)),
      map((): null => null),
    );
  }

  deleteCallQueue(callQueueId: string, name?: string): Observable<void> {
    return this._callQueuesResource.delete(this._tenantId, callQueueId).pipe(
      switchMap(() => this.refreshAllData()),
      tap(() => {
        if (name) {
          this._toastService.pushDeleteToast('tkey;teams_call_queues.list_view.toast.service_type', name);
        }
      }),
    );
  }

  createCallQueue(callQueue: MicrosoftTeamsCallQueue): Observable<void> {
    return this._callQueuesResource.post(this._tenantId, callQueue).pipe(switchMap(() => this.refreshAllData()));
  }

  updateCallQueue(callQueue: MicrosoftTeamsCallQueue): Observable<void> {
    return this._callQueuesResource.put(this._tenantId, callQueue).pipe(switchMap(() => this.refreshAllData()));
  }

  getCallQueue(callQueueId: string, tenantId: string): Observable<MicrosoftTeamsCallQueueView> {
    return this._callQueuesResource.getDetails(tenantId, callQueueId);
  }
}
